import { cn } from "@/lib/utils";
import { TONE_TEXT } from "@/lib/widget-status";
import { formatNOK } from "@/lib/format";
import type { InvoiceStatusFilter, InvoiceSummary } from "@/lib/invoice-list";

interface InvoiceSummaryCardsProps {
  summary: InvoiceSummary;
  /** Aktivt statusfilter, så kortet som filtrerer listen markeres. */
  activeStatus: InvoiceStatusFilter;
  onSelect: (status: "outstanding" | "overdue" | "draft") => void;
}

const fakturaer = (n: number) => `${n} ${n === 1 ? "faktura" : "fakturaer"}`;

/**
 * Oppsummering øverst på /faktura (VIB-2948). Utestående og Forfalt gjelder
 * alltid nå (inkl. mva); Fakturert i år er `org.total_revenue_ytd` (eks. mva).
 * Klikk filtrerer listen; klikk på aktivt kort fjerner filteret.
 */
export function InvoiceSummaryCards({ summary, activeStatus, onSelect }: InvoiceSummaryCardsProps) {
  const card = "kpi-card text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";
  const active = "ring-[3px] ring-inset ring-primary";

  return (
    <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
      <button
        type="button"
        className={cn(card, activeStatus === "outstanding" && active)}
        aria-pressed={activeStatus === "outstanding"}
        onClick={() => onSelect("outstanding")}
      >
        <div className="label">Utestående</div>
        <div className="value !text-[22px] sm:!text-[32px]">{formatNOK(summary.outstanding.amount)}</div>
        <div className="delta text-muted-foreground">{fakturaer(summary.outstanding.count)} · inkl. mva</div>
      </button>

      <button
        type="button"
        className={cn(card, activeStatus === "overdue" && active)}
        aria-pressed={activeStatus === "overdue"}
        onClick={() => onSelect("overdue")}
      >
        <div className="label">Forfalt</div>
        <div className={cn("value !text-[22px] sm:!text-[32px]", summary.overdue.count > 0 && TONE_TEXT.miss)}>
          {formatNOK(summary.overdue.amount)}
        </div>
        <div className="delta text-muted-foreground">
          {fakturaer(summary.overdue.count)}
          {summary.overdue.oldestDays != null && ` · eldste ${summary.overdue.oldestDays} dager`}
        </div>
      </button>

      <div className="kpi-card accent hidden sm:block">
        <div className="label">Fakturert i år</div>
        <div className="value !text-[32px]">{formatNOK(summary.invoicedYtd.amount)}</div>
        <div className="delta">{fakturaer(summary.invoicedYtd.count)} · eks. mva, kreditnota trukket fra</div>
      </div>

      <button
        type="button"
        className={cn(card, "hidden sm:block", activeStatus === "draft" && active)}
        aria-pressed={activeStatus === "draft"}
        onClick={() => onSelect("draft")}
      >
        <div className="label">Ikke sendt</div>
        <div className="value !text-[32px]">{summary.unsent}</div>
        <div className="delta text-muted-foreground">Utkast med send-feil</div>
      </button>
    </div>
  );
}
